import { useTables } from './TablesContext';
import { getOnStorage, setOnStorage } from '@/shared';
import { useState, useContext, createContext, useMemo, useEffect, useRef } from 'react';
import { CorporateFlagsTheme } from '@/types';
import { CORPORATE_COMPER, CORPORATE_FORT, CORPORATE_BF_SELLER } from '@/mocks';
import { useClient } from './ClientContext';
import * as R from '@/constants/systemRoutes';
import { parseJSON } from '@/shared';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

interface HookProps {
  brand: string;
  brands: CorporateFlagsTheme[];
  brandTheme: CorporateFlagsTheme;
  selectedBrandPair: SelectedBrandPair | null;
  setSelectedBrand: (brand: CorporateFlagsTheme) => void;
}

const routes = Object.values(R);

// const DEFAULT_CORPORATE_FLAG = CORPORATECOMPER;
// const DEFAULT_CORPORATE_FLAG = CORPORATE_FORT;
const DEFAULT_CORPORATE_FLAG = CORPORATE_BF_SELLER;
const BRAND_REDIRECT_DELAY_MS = 250;
const ENABLED_ROUTES = {
  [R.SUBCATEGORIES.id]: true,
  [R.CATEGORY.id]: true,
  [R.CATEGORIES.id]: true,
  [R.PRODUCT_PAGE.id]: true,
  [R.SEARCH.id]: true,
  [R.CART.id]: true,
  [R.ECOMMERCE.id]: true,
  [R.CHECKOUT.id]: true,
  [R.DASHBOARD.id]: true,
}

const mockedImages = {
  'BF Seller': CORPORATE_BF_SELLER.image,
  COMPER: CORPORATE_COMPER.image,
  'Fort Atacadista': CORPORATE_FORT.image,
}

const ConfigsContex = createContext<HookProps>({} as HookProps);
type BrandLojaSegmentoRule = {
  cod_loja?: string | number;
  cod_segmento?: string | number;
};

type SelectedBrandPair = {
  codLoja: number;
  codSegmento: number;
};

type CorporateFlagWithRules = CorporateFlagsTheme & {
  cod_loja?: BrandLojaSegmentoRule[];
};

type StoredSelectedBrand = {
  codLoja: number;
  codSegmento: number;
  brandKey?: string;
  brandName?: string;
};

type PendingBrandSelection = {
  data: StoredSelectedBrand;
  shouldRedirect: boolean;
};

const toNumber = (value: string | number | undefined | null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSegmentCode = (segmento: any) => {
  if (segmento == null) return null;
  return toNumber(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento);
};

const parseSelectedBrandPair = (value: unknown): SelectedBrandPair | null => {
  if (!value || typeof value !== 'object') return null;

  const codLoja = toNumber((value as any).codLoja);
  const codSegmento = toNumber((value as any).codSegmento);

  if (codLoja == null || codSegmento == null) return null;

  return { codLoja, codSegmento };
};

const parseStoredSelectedBrand = (value: unknown): StoredSelectedBrand | null => {
  if (!value || typeof value !== 'object') return null;

  const codLoja = toNumber((value as StoredSelectedBrand).codLoja);
  const codSegmento = toNumber((value as StoredSelectedBrand).codSegmento);
  if (codLoja == null || codSegmento == null) return null;

  return {
    codLoja,
    codSegmento,
    brandKey: typeof (value as StoredSelectedBrand).brandKey === 'string' ? (value as StoredSelectedBrand).brandKey : '',
    brandName: typeof (value as StoredSelectedBrand).brandName === 'string' ? (value as StoredSelectedBrand).brandName : '',
  };
};

const toBrandKey = (pair: SelectedBrandPair | null) => {
  if (!pair) return '';
  return `${pair.codLoja}:${pair.codSegmento}`;
};

const normalizeBrandPairs = (brand: CorporateFlagWithRules) => {
  const rules = Array.isArray(brand.cod_loja) ? brand.cod_loja : [];
  const normalized = rules
    .map((rule) => {
      const codLoja = toNumber(rule.cod_loja);
      const codSegmento = toNumber(rule.cod_segmento);
      if (codLoja == null || codSegmento == null) return null;
      return `${codLoja}:${codSegmento}`;
    })
    .filter(Boolean) as string[];

  return [...new Set(normalized)].sort();
};

const getBrandStorageKey = (brand: CorporateFlagWithRules | null) => {
  if (!brand) return '';
  const name = brand.name || 'brand';
  const pairs = normalizeBrandPairs(brand);

  if (!pairs.length) return name;

  return `${name}|${pairs.join(',')}`;
};

const notifySelectedBrandChange = () => {
  window.dispatchEvent(new Event('selected-brand-changed'));
};

const isSameStoredBrand = (a: StoredSelectedBrand | null, b: StoredSelectedBrand | null) => {
  if (!a || !b) return false;
  return a.codLoja === b.codLoja && a.codSegmento === b.codSegmento && (a.brandKey || '') === (b.brandKey || '');
};

const getBrandMatchByClient = (brandItem: CorporateFlagWithRules, clientPairs: SelectedBrandPair[]) => {
  const rules = Array.isArray(brandItem.cod_loja) ? brandItem.cod_loja : [];
  if (!rules.length || !clientPairs.length) return null;

  for (const rule of rules) {
    const codLoja = toNumber(rule.cod_loja);
    const codSegmento = toNumber(rule.cod_segmento);
    if (codLoja == null || codSegmento == null) continue;

    const hasMatch = clientPairs.some((pair) => pair.codLoja === codLoja && pair.codSegmento === codSegmento);
    if (hasMatch) return { codLoja, codSegmento };
  }

  return null;
};

const CorporateFlagsProvider: React.FC<any> = ({ children }) => {
  const locale = useLocation();
  const navigate = useNavigate();
  const { tables } = useTables();
  const { selectedClient } = useClient();
  const pendingBrandSelectionRef = useRef<PendingBrandSelection | null>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedBrandPair, setSelectedBrandPair] = useState<SelectedBrandPair | null>(() => {
    const selectedBrand = getOnStorage('selected_brand');
    const parsedStoredBrand = parseStoredSelectedBrand(selectedBrand);
    if (parsedStoredBrand) {
      return {
        codLoja: parsedStoredBrand.codLoja,
        codSegmento: parsedStoredBrand.codSegmento,
      };
    }
    return parseSelectedBrandPair(selectedBrand);
  });
  const [selectedBrandName, setSelectedBrandName] = useState(() => {
    const selectedBrand = getOnStorage('selected_brand');
    const parsedStoredBrand = parseStoredSelectedBrand(selectedBrand);
    return parsedStoredBrand?.brandName || '';
  });

  const clientPairs = useMemo(() => {
    const pairs = [] as SelectedBrandPair[];
    const lojas = selectedClient?.lojas || [];

    if (lojas.length > 0) {
      lojas.forEach((loja) => {
        const codLoja = toNumber(loja.COD_LOJA);
        const segmentos = Array.isArray(loja.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
        segmentos.forEach((segmento: any) => {
          const codSegmento = getSegmentCode(segmento);
          if (codLoja != null && codSegmento != null) {
            pairs.push({ codLoja, codSegmento });
          }
        });
      });

      return pairs;
    }

    const codLoja = toNumber(selectedClient?.COD_LOJA);
    const segmentos = Array.isArray(selectedClient?.COD_SEGMENTO) ? selectedClient.COD_SEGMENTO : [];
    segmentos.forEach((segmento: any) => {
      const codSegmento = getSegmentCode(segmento);
      if (codLoja != null && codSegmento != null) {
        pairs.push({ codLoja, codSegmento });
      }
    });

    return pairs;
  }, [selectedClient]);

  const awsParams = useMemo(() => {
    return tables.current?.parametros_aws?.slice().reverse()?.filter((param) => param.DESC_PARAMETRO === 'parametro_loja') || [];
  }, [tables.current?.parametros_aws]);

  const brands = useMemo(() => {
    const mergedByName = new Map<string, CorporateFlagWithRules>();

    awsParams.forEach((param) => {
      const jsonParams = parseJSON(param?.VALOR_PARAMETRO || '[]') || [];
      const listBrands: CorporateFlagWithRules[] = Array.isArray(jsonParams) ? jsonParams : [];

      listBrands.forEach((brandItem) => {
        if (!brandItem?.name) return;
        if (!mergedByName.has(brandItem.name)) {
          mergedByName.set(brandItem.name, brandItem);
        }
      });
    });

    const listBrands = [...mergedByName.values()];

    const filteredBrands = listBrands.filter((brandItem) => {
      const rules = Array.isArray(brandItem.cod_loja) ? brandItem.cod_loja : [];
      if (!rules.length || !clientPairs.length) return true;

      return rules.some((rule) => {
        const brandLoja = toNumber(rule.cod_loja);
        const brandSegmento = toNumber(rule.cod_segmento);
        if (brandLoja == null || brandSegmento == null) return false;

        return clientPairs.some((pair) => pair.codLoja === brandLoja && pair.codSegmento === brandSegmento);
      });
    });

    return filteredBrands.map((brandItem) => {
      if (!brandItem.image) {
        brandItem.image = mockedImages[brandItem.name as keyof typeof mockedImages] || '';
      }
      return brandItem as CorporateFlagsTheme;
    });
  }, [awsParams, clientPairs]);

  useEffect(() => {
    if (!clientPairs.length) {
      return;
    }

    const hasCurrentPair = !!selectedBrandPair && clientPairs.some(
      (pair) => pair.codLoja === selectedBrandPair.codLoja && pair.codSegmento === selectedBrandPair.codSegmento
    );

    if (hasCurrentPair) return;

    const storedSelectedBrand = parseStoredSelectedBrand(getOnStorage('selected_brand'));
    const storedPair = storedSelectedBrand
      ? clientPairs.find(
        (pair) => pair.codLoja === storedSelectedBrand.codLoja && pair.codSegmento === storedSelectedBrand.codSegmento
      )
      : null;

    const nextPair = storedPair || clientPairs[0];
    const nextBrandByStorageName = storedSelectedBrand?.brandName
      ? brands.find((brandItem) => brandItem.name === storedSelectedBrand.brandName)
      : null;
    const fallbackBrand = (nextBrandByStorageName || brands.find((brandItem) => {
      const matchedPair = getBrandMatchByClient(brandItem as CorporateFlagWithRules, [nextPair]);
      return !!matchedPair;
    }) || brands[0]) as CorporateFlagWithRules | undefined;

    setSelectedBrandPair(nextPair);
    setSelectedBrandName(fallbackBrand?.name || '');

    if (fallbackBrand) {
      setOnStorage('selected_brand', {
        codLoja: nextPair.codLoja,
        codSegmento: nextPair.codSegmento,
        brandName: fallbackBrand.name || '',
        brandKey: getBrandStorageKey(fallbackBrand),
      });
      notifySelectedBrandChange();
    }
  }, [brands, clientPairs, selectedBrandPair]);

  const routeID = useMemo(() => {
    const sortedRoutes = routes
      .filter((route) => !!route?.link)
      .sort((a, b) => {
        const aHasWildcard = a.link.includes('*');
        const bHasWildcard = b.link.includes('*');
        if (aHasWildcard !== bHasWildcard) return aHasWildcard ? 1 : -1;

        const aHasParams = a.link.includes(':');
        const bHasParams = b.link.includes(':');
        if (aHasParams !== bHasParams) return aHasParams ? 1 : -1;

        return b.link.length - a.link.length;
      });

    const matchedRoute = sortedRoutes.find((route) => {
      return !!matchPath({ path: route.link, end: true }, locale.pathname);
    });

    return matchedRoute?.id || '';
  }, [locale.pathname]);

  const enableBrand = useMemo(() => {
    return !!selectedClient?.COD_CLIENTE && ENABLED_ROUTES[routeID];
  }, [selectedClient, routeID]);

  const brandTheme = useMemo(() => {
    if (!enableBrand) return {} as CorporateFlagsTheme;

    if (selectedBrandName) {
      const selectedByName = brands.find((brandItem) => brandItem.name === selectedBrandName);
      if (selectedByName) return selectedByName;
    }

    if (!selectedBrandPair) return brands[0] || DEFAULT_CORPORATE_FLAG;

    return brands.find((brandItem) => {
      const matchedPair = getBrandMatchByClient(brandItem as CorporateFlagWithRules, [selectedBrandPair]);
      return !!matchedPair;
    }) || brands[0] || DEFAULT_CORPORATE_FLAG;
  }, [brands, selectedBrandPair, selectedBrandName, enableBrand]);

  const selectedBrandData = useMemo(() => {
    if (!selectedBrandPair) return null;

    const selectedByName = selectedBrandName
      ? brands.find((brandItem) => brandItem.name === selectedBrandName)
      : null;

    const selectedBrand = (selectedByName || brands.find((brandItem) => {
      const matchedPair = getBrandMatchByClient(brandItem as CorporateFlagWithRules, [selectedBrandPair]);
      return !!matchedPair;
    })) as CorporateFlagWithRules | undefined;

    if (!selectedBrand) return null;

    return {
      codLoja: selectedBrandPair.codLoja,
      codSegmento: selectedBrandPair.codSegmento,
      brandName: selectedBrand.name || '',
      brandKey: getBrandStorageKey(selectedBrand),
    };
  }, [brands, selectedBrandPair, selectedBrandName]);

  useEffect(() => {
    if (!selectedBrandData) return;

    const currentStored = parseStoredSelectedBrand(getOnStorage('selected_brand'));
    const hasSameData = isSameStoredBrand(currentStored, selectedBrandData);

    if (!hasSameData) {
      setOnStorage('selected_brand', selectedBrandData);
      notifySelectedBrandChange();
    }

    const pendingSelection = pendingBrandSelectionRef.current;
    if (!pendingSelection) return;

    const hasConfirmedSelectedBrand = isSameStoredBrand(selectedBrandData, pendingSelection.data);
    if (!hasConfirmedSelectedBrand) return;

    pendingBrandSelectionRef.current = null;

    if (pendingSelection.shouldRedirect && routeID === R.PRODUCT_PAGE.id) {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }

      redirectTimeoutRef.current = setTimeout(() => {
        navigate(R.ECOMMERCE.link);
        redirectTimeoutRef.current = null;
      }, BRAND_REDIRECT_DELAY_MS);
    }
  }, [selectedBrandData, routeID, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const setSelectedBrand = (brand: CorporateFlagsTheme) => {
    const matchedPair = getBrandMatchByClient(brand as CorporateFlagWithRules, clientPairs);
    if (!matchedPair) return;

    const selectedBrandData: StoredSelectedBrand = {
      codLoja: matchedPair.codLoja,
      codSegmento: matchedPair.codSegmento,
      brandName: brand.name || '',
      brandKey: getBrandStorageKey(brand as CorporateFlagWithRules),
    };
    pendingBrandSelectionRef.current = {
      data: selectedBrandData,
      shouldRedirect: routeID === R.PRODUCT_PAGE.id,
    };

    setSelectedBrandPair(matchedPair);
    setSelectedBrandName(brand.name || '');
    setOnStorage('selected_brand', selectedBrandData);
    notifySelectedBrandChange();
  };
  return (
    <ConfigsContex.Provider
      value={{
        brand: toBrandKey(selectedBrandPair),
        brands,
        brandTheme,
        selectedBrandPair,
        setSelectedBrand,
      }}
    >
      {children}
    </ConfigsContex.Provider>
  );
};

const useCorporateFlags = (): HookProps => {
  const ctx = useContext(ConfigsContex);

  if (!ctx) throw new Error('Erro ao usar hook useCorporateFlags');

  return ctx;
};

export { CorporateFlagsProvider, useCorporateFlags };
